import { useState } from "react";
import { Ring } from "./Ring";
import { sc, scLabel, fmt$ } from "./reportUtils";
import { MapPin, Star, AlertTriangle, TrendingDown, Search, AlertCircle, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function getPhotoUrl(photos) {
  if (!photos?.length) return "";
  const ref = photos[0]?.photo_reference;
  if (!ref) return "";
  return `${API}/api/places/photo?maxwidth=1200&photo_reference=${encodeURIComponent(ref)}`;
}

export default function PhotoScoreHeader({ business, scores, issues, revenue_impact, score_zone, issueCards }) {
  const overall = scores?.overall || 0;
  // Use frontend-generated issueCards (same array IssueCardsSection renders) as the single source of truth
  const totalFindings = issueCards ? issueCards.length : (issues || []).length;
  const criticalCount = issueCards ? issueCards.filter(c => c.impact === "high").length : (issues || []).filter(i => ["high", "critical"].includes(i.priority)).length;
  const loss = revenue_impact?.monthly_revenue_loss || 0;
  const missedPct = revenue_impact?.missed_pct || (100 - overall);
  const photoUrl = getPhotoUrl(business?.photos);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [showCounter, setShowCounter] = useState(false);

  const hasPhoto = photoUrl && !imgError;

  const dataPoints = [
    { label: "GBP Signals", count: 52 },
    { label: "Local Rankings", count: 42 },
    { label: "Citation Health", count: 68 },
    { label: "Website/Technical", count: 54 },
    { label: "Reviews/Reputation", count: 43 },
    { label: "AI Search", count: 18 },
    { label: "Social Presence", count: 23 },
  ];
  const totalPoints = dataPoints.reduce((s, d) => s + d.count, 0);

  return (
    <section id="hero" data-testid="photo-score-header" style={{ position: "relative", overflow: "hidden", marginBottom: 24 }}>
      {/* Photo background */}
      {hasPhoto && (
        <>
          <img
            src={photoUrl}
            alt={business?.name || "Business"}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center 30%",
              opacity: imgLoaded ? 1 : 0, transition: "opacity 0.6s ease",
            }}
          />
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(180deg, rgba(15,23,42,0.5) 0%, rgba(15,23,42,0.78) 50%, rgba(15,23,42,0.92) 100%)",
          }} />
        </>
      )}

      {/* Solid dark fallback */}
      {!hasPhoto && (
        <div style={{
          position: "absolute", inset: 0,
          background: "#0F172A",
        }} />
      )}

      {/* Content */}
      <div style={{ position: "relative", zIndex: 2, padding: "24px 16px 20px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
          {/* Score ring */}
          <Ring score={overall} size={110} stroke={9} dark />

          {/* Business info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".2em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 6, fontFamily: "'IBM Plex Sans',sans-serif" }}>
              LOCAL SEO AUDIT REPORT
            </p>
            <h1 data-testid="business-name-header" style={{ fontSize: "clamp(22px, 5vw, 32px)", fontWeight: 700, color: "#fff", margin: "0 0 6px", fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.02em", wordBreak: "break-word" }}>
              {business?.name}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <MapPin size={12} style={{ color: "rgba(255,255,255,0.45)", flexShrink: 0 }} />
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'IBM Plex Sans',sans-serif" }}>{business?.address}</p>
            </div>

            {/* Rating */}
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  fill={i < Math.round(business?.rating || 0) ? "#FBBF24" : "transparent"}
                  style={{ color: i < Math.round(business?.rating || 0) ? "#FBBF24" : "rgba(255,255,255,0.2)" }}
                />
              ))}
              <span style={{ color: "#fff", fontSize: 13, fontWeight: 700, marginLeft: 4, fontFamily: "'JetBrains Mono',monospace" }}>{business?.rating}</span>
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, fontFamily: "'IBM Plex Sans',sans-serif" }}>({(business?.review_count || 0).toLocaleString()})</span>
            </div>

            {/* Score zone badge + data counter */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: sc(overall), background: "rgba(0,0,0,0.4)", padding: "5px 12px", letterSpacing: ".04em", fontFamily: "'IBM Plex Sans',sans-serif" }}>
                {score_zone?.label || scLabel(overall)}
              </span>
              <div
                data-testid="audit-counter"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.08)", padding: "5px 12px", cursor: "pointer" }}
                onMouseEnter={() => setShowCounter(true)}
                onMouseLeave={() => setShowCounter(false)}
                onClick={() => setShowCounter(p => !p)}
              >
                <Check size={10} style={{ color: "#22c55e" }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.6)", fontFamily: "'JetBrains Mono',monospace" }}>
                  {totalPoints} data points
                </span>
              </div>
            </div>
            {showCounter && (
              <div style={{
                marginTop: 8, background: "rgba(15,23,42,0.95)", backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.1)", padding: "14px 18px", minWidth: 220, maxWidth: 280,
              }}>
                {dataPoints.map((dp, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontFamily: "'IBM Plex Sans',sans-serif" }}>{dp.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.8)", fontFamily: "'JetBrains Mono',monospace" }}>{dp.count}</span>
                  </div>
                ))}
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "'IBM Plex Sans',sans-serif" }}>Total</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#22c55e", fontFamily: "'JetBrains Mono',monospace" }}>{totalPoints}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* KPI cards — Bento grid with left accent borders */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 20 }}>
          {[
            { value: fmt$(loss), label: "Monthly Loss", color: "#E11D48", icon: TrendingDown },
            { value: String(criticalCount), label: "Critical Issues", color: "#E11D48", icon: AlertTriangle },
            { value: `${missedPct}%`, label: "Searches Lost", color: "#D97706", icon: Search },
            { value: String(totalFindings), label: "Total Findings", color: "#D97706", icon: AlertCircle },
          ].map((card, i) => (
            <div key={i} style={{
              background: "rgba(255,255,255,0.06)", backdropFilter: "blur(8px)",
              borderLeft: `3px solid ${card.color}`, padding: "14px 14px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <card.icon size={12} style={{ color: card.color, opacity: 0.7 }} />
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontWeight: 600, margin: 0, letterSpacing: ".06em", textTransform: "uppercase", fontFamily: "'IBM Plex Sans',sans-serif" }}>{card.label}</p>
              </div>
              <p style={{ fontSize: 22, fontWeight: 700, color: "#fff", lineHeight: 1, margin: 0, fontFamily: "'Outfit',sans-serif" }}>{card.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
